import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
    FileText, Search, Download, Mail, MailX, ChevronLeft, ChevronRight,
    Loader2, FilterX, ScrollText, Trash2, RotateCcw, Ban, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import EmptyTrashButton from "@/components/admin/EmptyTrashButton";

const PAGE_SIZE = 25;

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric", month: "short", day: "numeric",
        });
    } catch {
        return iso;
    }
}

function DocTypeBadge({ type }) {
    const isCol = (type || "").toLowerCase() === "col";
    return (
        <Badge
            variant="outline"
            className={
                isCol
                    ? "border-accent/60 text-accent-foreground bg-accent/15 uppercase text-[10px] tracking-wider"
                    : "border-primary/50 text-primary bg-primary/8 uppercase text-[10px] tracking-wider"
            }
            data-testid={`loa-row-type-${isCol ? "col" : "loa"}`}
        >
            {isCol ? "COL" : "LOA"}
        </Badge>
    );
}

function EmailedPill({ emailedTo, emailedAt }) {
    if (!emailedTo) {
        return (
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <MailX className="h-3 w-3" /> Not sent
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 text-[11px] text-primary">
            <Mail className="h-3 w-3" />
            <span className="truncate max-w-[180px]" title={`Sent ${fmtDate(emailedAt)} to ${emailedTo}`}>
                {emailedTo}
            </span>
        </span>
    );
}

function KpiTile({ label, value, Icon }) {
    return (
        <Card className="surface-card border-border">
            <CardContent className="p-5">
                <div className="flex items-center justify-between">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                        {label}
                    </div>
                    {Icon && (
                        <div className="h-8 w-8 rounded-md bg-primary/8 text-primary flex items-center justify-center">
                            <Icon className="h-4 w-4" />
                        </div>
                    )}
                </div>
                <div className="font-display text-3xl text-foreground mt-2 leading-none">
                    {value}
                </div>
            </CardContent>
        </Card>
    );
}

export default function AdminLoaRegistry() {
    const { user } = useAuth();
    const isSuper = user?.role === "super_admin";

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [trashTotal, setTrashTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [tab, setTab] = useState("active");  // "active" | "trash"
    const [selected, setSelected] = useState(new Set());
    const [bulkSubmitting, setBulkSubmitting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);  // row pending single-delete

    const [q, setQ] = useState("");
    const [docType, setDocType] = useState("all");
    const [programId, setProgramId] = useState("all");
    const [emailed, setEmailed] = useState("all");
    const [issuedFrom, setIssuedFrom] = useState("");
    const [issuedTo, setIssuedTo] = useState("");

    const [programs, setPrograms] = useState([]);
    const [downloadingId, setDownloadingId] = useState(null);

    const offset = page * PAGE_SIZE;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: PAGE_SIZE, offset };
            if (tab === "trash") params.only_deleted = true;
            if (q.trim()) params.q = q.trim();
            if (docType !== "all") params.document_type = docType;
            if (programId !== "all") params.program_id = programId;
            if (emailed === "sent") params.emailed = true;
            if (emailed === "unsent") params.emailed = false;
            if (issuedFrom) params.issued_from = issuedFrom;
            if (issuedTo) params.issued_to = issuedTo;
            const { data } = await api.get("/admin/loa", { params });
            setItems(data.items || []);
            setTotal(data.total || 0);
            setSelected(new Set());
        } catch {
            toast.error("Could not load the LOA registry. Try again.");
        } finally {
            setLoading(false);
        }
    }, [offset, q, docType, programId, emailed, issuedFrom, issuedTo, tab]);

    // Keep the trash-tab count fresh independently so we can show "Trash (N)"
    // in the tab pill and let the EmptyTrashButton render with an accurate count.
    const refreshTrashCount = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/loa", {
                params: { only_deleted: true, limit: 1, offset: 0 },
            });
            setTrashTotal(data.total || 0);
        } catch {
            /* non-fatal */
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { refreshTrashCount(); }, [refreshTrashCount, items.length]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/programs");
                if (cancelled) return;
                const rows = Array.isArray(data) ? data : data?.programs || [];
                setPrograms(rows.filter((p) => !p.is_deleted));
            } catch {
                /* non-fatal */
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const onSearchSubmit = (e) => {
        e.preventDefault();
        setPage(0);
        load();
    };

    const resetFilters = () => {
        setQ("");
        setDocType("all");
        setProgramId("all");
        setEmailed("all");
        setIssuedFrom("");
        setIssuedTo("");
        setPage(0);
    };

    const downloadPdf = useCallback(async (row) => {
        setDownloadingId(row.id);
        try {
            // Use the configured axios client so the Authorization header,
            // baseURL and 401-handling all work the same way as the rest of
            // the app. ``responseType: "blob"`` is required so axios doesn't
            // try to JSON-parse the binary PDF body.
            const res = await api.get(`/admin/loa/${row.id}/download`, {
                responseType: "blob",
            });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${row.loa_number || "letter-of-acceptance"}.pdf`;
            a.rel = "noopener";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            // Give the browser a tick before revoking the blob URL — some
            // download managers re-fetch the URL after the click event has
            // already returned.
            setTimeout(() => URL.revokeObjectURL(url), 4000);
        } catch (e) {
            const msg = e?.response?.status === 404
                ? "PDF file is missing in storage. Re-issue the document to regenerate it."
                : e?.response?.status === 502
                    ? "Could not reach storage. Try again in a moment."
                    : "Download failed. Try again in a moment.";
            toast.error(msg);
        } finally {
            setDownloadingId(null);
        }
    }, []);

    const toggleRow = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        setSelected((prev) =>
            prev.size === items.length ? new Set() : new Set(items.map((r) => r.id)),
        );
    };

    const bulkAct = useCallback(async (action) => {
        if (!isSuper || selected.size === 0) return;
        setBulkSubmitting(true);
        try {
            const { data } = await api.post("/admin/loa/bulk", {
                ids: Array.from(selected),
                action,
            });
            const okCount = data?.ok ?? 0;
            const verb = action === "delete" ? "moved to trash"
                : action === "restore" ? "restored"
                    : "permanently deleted";
            toast.success(`${okCount} document${okCount === 1 ? "" : "s"} ${verb}.`);
            setSelected(new Set());
            await load();
            await refreshTrashCount();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Bulk action failed.";
            toast.error(typeof msg === "string" ? msg : "Bulk action failed.");
        } finally {
            setBulkSubmitting(false);
        }
    }, [isSuper, selected, load, refreshTrashCount]);

    const deleteOne = useCallback(async (row) => {
        try {
            await api.delete(`/admin/loa/${row.id}`);
            toast.success("Moved to trash.");
            await load();
            await refreshTrashCount();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed.");
        } finally {
            setConfirmDelete(null);
        }
    }, [load, refreshTrashCount]);

    const restoreOne = useCallback(async (row) => {
        try {
            await api.post(`/admin/loa/${row.id}/restore`);
            toast.success("Restored.");
            await load();
            await refreshTrashCount();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Restore failed.");
        }
    }, [load, refreshTrashCount]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const showingFrom = total === 0 ? 0 : offset + 1;
    const showingTo = Math.min(offset + PAGE_SIZE, total);

    const kpis = useMemo(() => {
        const loaCount = items.filter((r) => (r.document_type || "loa") === "loa").length;
        const colCount = items.filter((r) => r.document_type === "col").length;
        const sentCount = items.filter((r) => r.emailed_to).length;
        return { loaCount, colCount, sentCount };
    }, [items]);

    const filtersActive =
        !!q || docType !== "all" || programId !== "all" || emailed !== "all" || !!issuedFrom || !!issuedTo;

    return (
        <div
            className="max-w-7xl mx-auto px-6 py-10 pb-24 space-y-6"
            data-testid="admin-loa-registry"
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl text-foreground flex items-center gap-3">
                        <ScrollText className="h-7 w-7 text-primary" />
                        LOA &amp; COL Registry
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Every Letter of Acceptance and Conditional Offer Letter ever issued.
                        Search by student, filter by program or date, and download the original PDF for audit.
                    </p>
                </div>
                <Badge
                    variant="outline"
                    className="border-primary/40 text-primary bg-primary/5 uppercase text-[10px] tracking-wider"
                >
                    {total} record{total === 1 ? "" : "s"} on file
                </Badge>
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap">
                <Tabs
                    value={tab}
                    onValueChange={(v) => { setTab(v); setPage(0); setSelected(new Set()); }}
                >
                    <TabsList data-testid="loa-tabs">
                        <TabsTrigger value="active" data-testid="loa-tab-active">
                            Active{tab === "active" ? ` (${total})` : ""}
                        </TabsTrigger>
                        <TabsTrigger value="trash" data-testid="loa-tab-trash">
                            Trash ({trashTotal})
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
                {tab === "trash" && (
                    <EmptyTrashButton
                        resource="loa"
                        count={trashTotal}
                        label="trashed documents"
                        onEmptied={() => {
                            setItems([]);
                            setTotal(0);
                            setTrashTotal(0);
                            setSelected(new Set());
                        }}
                        testId="loa-empty-trash"
                    />
                )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <KpiTile label="LOAs (this page)" value={kpis.loaCount} Icon={FileText} />
                <KpiTile label="COLs (this page)" value={kpis.colCount} Icon={FileText} />
                <KpiTile label="Emailed to student" value={kpis.sentCount} Icon={Mail} />
            </div>

            <Card className="surface-card border-border">
                <CardContent className="p-5 space-y-4">
                    <form
                        onSubmit={onSearchSubmit}
                        className="flex flex-col lg:flex-row gap-3 lg:items-end"
                    >
                        <div className="flex-1 min-w-[200px]">
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1.5">
                                Search
                            </label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Student name, email, ID, or LOA number…"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    className="pl-9"
                                    data-testid="loa-search-input"
                                />
                            </div>
                        </div>

                        <div className="w-full lg:w-[150px]">
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1.5">
                                Document
                            </label>
                            <Select value={docType} onValueChange={(v) => { setDocType(v); setPage(0); }}>
                                <SelectTrigger data-testid="loa-filter-doctype"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All types</SelectItem>
                                    <SelectItem value="loa">LOA only</SelectItem>
                                    <SelectItem value="col">COL only</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="w-full lg:w-[220px]">
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1.5">
                                Program
                            </label>
                            <Select value={programId} onValueChange={(v) => { setProgramId(v); setPage(0); }}>
                                <SelectTrigger data-testid="loa-filter-program"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All programs</SelectItem>
                                    {programs.map((p) => (
                                        <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="w-full lg:w-[150px]">
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1.5">
                                Emailed
                            </label>
                            <Select value={emailed} onValueChange={(v) => { setEmailed(v); setPage(0); }}>
                                <SelectTrigger data-testid="loa-filter-emailed"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Any status</SelectItem>
                                    <SelectItem value="sent">Sent</SelectItem>
                                    <SelectItem value="unsent">Not sent</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="w-full lg:w-[155px]">
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1.5">
                                Issued from
                            </label>
                            <Input
                                type="date"
                                value={issuedFrom}
                                onChange={(e) => { setIssuedFrom(e.target.value); setPage(0); }}
                                data-testid="loa-filter-from"
                            />
                        </div>

                        <div className="w-full lg:w-[155px]">
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1.5">
                                Issued to
                            </label>
                            <Input
                                type="date"
                                value={issuedTo}
                                onChange={(e) => { setIssuedTo(e.target.value); setPage(0); }}
                                data-testid="loa-filter-to"
                            />
                        </div>

                        <div className="flex gap-2">
                            <Button type="submit" data-testid="loa-search-submit">
                                <Search className="h-4 w-4 mr-1.5" /> Apply
                            </Button>
                            {filtersActive && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={resetFilters}
                                    data-testid="loa-filters-reset"
                                >
                                    <FilterX className="h-4 w-4 mr-1.5" /> Reset
                                </Button>
                            )}
                        </div>
                    </form>
                </CardContent>
            </Card>

            <Card className="surface-card border-border">
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {isSuper && (
                                    <TableHead className="w-[44px]">
                                        <Checkbox
                                            checked={items.length > 0 && selected.size === items.length}
                                            onCheckedChange={toggleAll}
                                            aria-label="Select all visible documents"
                                            data-testid="loa-select-all"
                                            disabled={items.length === 0}
                                        />
                                    </TableHead>
                                )}
                                <TableHead className="w-[110px]">Type</TableHead>
                                <TableHead>Document #</TableHead>
                                <TableHead>Student</TableHead>
                                <TableHead>Program</TableHead>
                                <TableHead>Issued</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead className="text-right">Action</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={isSuper ? 8 : 7} className="text-center py-12 text-muted-foreground">
                                        <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
                                        Loading registry…
                                    </TableCell>
                                </TableRow>
                            ) : items.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={isSuper ? 8 : 7} className="text-center py-12 text-muted-foreground">
                                        {tab === "trash"
                                            ? "Trash is empty."
                                            : "No documents match these filters yet."}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                items.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        data-testid={`loa-row-${row.id}`}
                                        className={selected.has(row.id) ? "bg-primary/4" : undefined}
                                    >
                                        {isSuper && (
                                            <TableCell>
                                                <Checkbox
                                                    checked={selected.has(row.id)}
                                                    onCheckedChange={() => toggleRow(row.id)}
                                                    aria-label={`Select ${row.loa_number}`}
                                                    data-testid={`loa-select-${row.id}`}
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell><DocTypeBadge type={row.document_type} /></TableCell>
                                        <TableCell className="font-mono text-xs">{row.loa_number}</TableCell>
                                        <TableCell>
                                            <div className="font-medium text-sm">{row.student_name || "—"}</div>
                                            <div className="text-[11px] text-muted-foreground truncate max-w-[260px]">
                                                {row.student_email || row.student_id || ""}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm">{row.program_title || "—"}</TableCell>
                                        <TableCell className="text-sm">{fmtDate(row.issued_at)}</TableCell>
                                        <TableCell>
                                            <EmailedPill emailedTo={row.emailed_to} emailedAt={row.emailed_at} />
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="inline-flex gap-1.5">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => downloadPdf(row)}
                                                    disabled={downloadingId === row.id}
                                                    data-testid={`loa-download-${row.id}`}
                                                >
                                                    {downloadingId === row.id ? (
                                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                    ) : (
                                                        <>
                                                            <Download className="h-3.5 w-3.5 mr-1" /> PDF
                                                        </>
                                                    )}
                                                </Button>
                                                {isSuper && tab === "active" && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => setConfirmDelete(row)}
                                                        className="border-destructive/40 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                                                        data-testid={`loa-delete-${row.id}`}
                                                        title="Move to trash"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                )}
                                                {isSuper && tab === "trash" && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => restoreOne(row)}
                                                        className="border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground"
                                                        data-testid={`loa-restore-${row.id}`}
                                                        title="Restore"
                                                    >
                                                        <RotateCcw className="h-3.5 w-3.5" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>

                    <div className="flex items-center justify-between p-4 border-t border-border">
                        <div className="text-xs text-muted-foreground">
                            {total === 0
                                ? "0 records"
                                : `Showing ${showingFrom}–${showingTo} of ${total}`}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page === 0 || loading}
                                onClick={() => setPage((p) => Math.max(0, p - 1))}
                                data-testid="loa-page-prev"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <span className="text-xs text-muted-foreground">
                                Page {page + 1} / {totalPages}
                            </span>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page + 1 >= totalPages || loading}
                                onClick={() => setPage((p) => p + 1)}
                                data-testid="loa-page-next"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {isSuper && selected.size > 0 && (
                <div
                    className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-card border border-border shadow-xl rounded-md px-4 py-3 flex items-center gap-3"
                    data-testid="loa-bulk-action-bar"
                >
                    <span className="text-sm font-medium">
                        {selected.size} document{selected.size === 1 ? "" : "s"} selected
                    </span>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelected(new Set())}
                        disabled={bulkSubmitting}
                        data-testid="loa-bulk-clear"
                    >
                        Clear
                    </Button>
                    {tab === "active" ? (
                        <Button
                            size="sm"
                            onClick={() => bulkAct("delete")}
                            disabled={bulkSubmitting}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid="loa-bulk-trash"
                        >
                            {bulkSubmitting ? (
                                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                            ) : (
                                <Trash2 className="h-4 w-4 mr-1.5" />
                            )}
                            Move to Trash
                        </Button>
                    ) : (
                        <>
                            <Button
                                size="sm"
                                onClick={() => bulkAct("restore")}
                                disabled={bulkSubmitting}
                                data-testid="loa-bulk-restore"
                            >
                                {bulkSubmitting ? (
                                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                                ) : (
                                    <RotateCcw className="h-4 w-4 mr-1.5" />
                                )}
                                Restore
                            </Button>
                            <Button
                                size="sm"
                                onClick={() => bulkAct("purge")}
                                disabled={bulkSubmitting}
                                className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                                data-testid="loa-bulk-purge"
                            >
                                <Ban className="h-4 w-4 mr-1.5" />
                                Delete forever
                            </Button>
                        </>
                    )}
                </div>
            )}

            <AlertDialog
                open={!!confirmDelete}
                onOpenChange={(o) => !o && setConfirmDelete(null)}
            >
                <AlertDialogContent data-testid="loa-confirm-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 font-display">
                            <ShieldAlert className="h-5 w-5 text-destructive" />
                            Move to trash?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="font-mono">{confirmDelete?.loa_number}</span>{" "}
                            for <strong>{confirmDelete?.student_name}</strong> will be moved
                            to the LOA trash. The audit log will record this action and the
                            stored PDF stays intact. You can restore it from the Trash tab.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="loa-confirm-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => confirmDelete && deleteOne(confirmDelete)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="loa-confirm-delete-ok"
                        >
                            Move to trash
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
